import { Flex, useMediaQuery } from '@chakra-ui/react';
import {
  MainFilter,
  SearchFilter,
  SortByFilter,
  TableCardToggle,
} from './components';
import { FilterContainer } from '@/components/filters_revamp';

// Chakra's `md`. Kept literal because `useMediaQuery` takes a raw query.
const MD_BREAKPOINT = '48em';
// The matching max-width for rules that must stop before `md` starts.
const COMPACT_MAX_WIDTH = '47.99em';

export const Filter = ({
  configs = [],
  activeFilters,
  isSortEnabled = false,
  sortOptions = [],
  sort = {},
  isSearchEnabled = false,
  hideSearchFullView = false,
  rightAdditionalContent = null,
  searchTerm = '',
  enableToggleView = false,
  toggleList = undefined,
  currentView = undefined,
  filterActions,
  mainFilterProps = {},
  CustomFilters = () => <></>,
  RightCustomFilters = () => <></>,
  onSave: _onSave = () => {},
  selected = [],
  filtersToShow = [],
  customFilters = [],
  isNewFilter = false,
  filterConfig = {},
  accordionTitle = undefined,
  newFilterStyles = false,
  showAccordion = true,
  showSearchInput = true,
  searchProps = {},
  disabled = false,
  sortMenuItemStyles = {},
  filterContainerProps = {},
}) => {
  // Whether the search shows as an icon is a render branch inside SearchFilter,
  // not something CSS can drive — so it needs a real boolean. `useMediaQuery`
  // rather than `useBreakpointValue`: the latter reads `theme.__breakpoints`,
  // which is null under the test theme and throws.
  const [isWide] = useMediaQuery(`(min-width: ${MD_BREAKPOINT})`);
  const isCompact = !isWide;

  return (
    <Flex
      alignItems="center"
      columnGap="8px"
      rowGap="12px"
      flexWrap="wrap"
      data-test-id="filters-container"
    >
      <Flex
        alignItems="center"
        alignSelf="center"
        // Grow so the right-hand controls are pushed to the opposite edge
        // (replaces the container's old `space-between`) — but never over a
        // width the caller pinned, since grow would otherwise beat it.
        // Below md it must not grow at all: it would split the row's free
        // space with the expanded search, leaving the trigger ~155px wide and
        // the search too narrow to use.
        flex={{
          base: '0 0 auto',
          md:
            filterContainerProps?.width || filterContainerProps?.w
              ? '0 1 auto'
              : '1 1 auto',
        }}
        {...filterContainerProps}
        data-test-id="filters-left-container"
      >
        {isNewFilter ? (
          <FilterContainer
            filterActions={filterActions}
            selected={selected}
            filtersToShow={filtersToShow}
            customFilters={customFilters}
            filterConfig={filterConfig}
            accordionTitle={accordionTitle}
            showAccordion={showAccordion}
            showSearchInput={showSearchInput}
            newFilterStyles={newFilterStyles}
            disabled={disabled}
          />
        ) : (
          <MainFilter
            mainFilterProps={mainFilterProps}
            configs={configs}
            filterActions={filterActions}
            activeFilters={activeFilters}
          />
        )}

        <CustomFilters />
      </Flex>
      <Flex
        alignItems="center"
        gap=".5rem"
        flexWrap="wrap"
        justifyContent="flex-end"
        // Absorbs the free space so this cluster and the search sit at the
        // right edge, the way the container's old `space-between` put them.
        // The left container's grow already does this when it is allowed to
        // grow; this is what covers the case where a caller pinned its width.
        ml="auto"
        // Children keep their size and wrap onto new lines instead of squishing
        // (which made the sort label word-wrap and the search overflow).
        sx={{ '& > *': { flexShrink: 0, maxWidth: '100%' } }}
        data-test-id="filters-right-container"
      >
        <RightCustomFilters />
        {isSortEnabled && (
          <SortByFilter
            sort={sort}
            options={sortOptions}
            filterActions={filterActions}
            menuItemStyles={sortMenuItemStyles}
          />
        )}
        {rightAdditionalContent}
        {enableToggleView && (
          <TableCardToggle
            toggleList={toggleList}
            currentView={currentView}
            filterActions={filterActions}
          />
        )}
      </Flex>
      {isSearchEnabled && (
        // Below md the search collapses to its icon and stays on the controls
        // row. Expanded, it should take the space left on that row rather than
        // wrap: `:has(input)` grows the container only once the input is
        // actually rendered, so the collapsed icon still takes no more room
        // than it needs. Scoped below md so desktop keeps its fixed 328px and
        // the right cluster keeps its `ml="auto"`.
        <Flex
          alignSelf="center"
          justifyContent="flex-end"
          maxW="100%"
          sx={{
            [`@media (max-width: ${COMPACT_MAX_WIDTH})`]: {
              // Basis 0, not auto: wrapping is decided from the item's
              // hypothetical size, and with `auto` that comes from the input's
              // `width: 100%` — so the row wrapped before grow ever applied.
              // At 0 it always fits, then grows into whatever is left.
              '&:has(input)': { flex: '1 1 0', minWidth: 0 },
            },
          }}
          data-test-id="filters-search-container"
        >
          <SearchFilter
            filterActions={filterActions}
            searchTerm={searchTerm}
            hideSearchFullView={hideSearchFullView || isCompact}
            inputGroupStyles={{
              // `w`, not `width`: callers override with the `w` shorthand, and
              // a differing key would leave this responsive value in place —
              // its md rule then beats their base one on desktop.
              w: { base: '100%', md: '328px' },
              maxWidth: '100%',
              ...searchProps.inputGroupStyles,
            }}
            placeholder={searchProps.placeholder}
          />
        </Flex>
      )}
    </Flex>
  );
};
